'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useFieldVariant } from '@/components/ui/field-variant';
import { ROTARY_FOCUS_AREAS, type RotaryFocusAreaDefinition } from '@/lib/rotary-focus-areas';
import { fieldChrome } from '@/lib/field-chrome';
import { cn } from '@/lib/utils';

interface RotaryFocusAreasMultiSelectProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

function toggle(ids: string[], id: string, checked: boolean): string[] {
  if (checked) {
    return ids.includes(id) ? ids : [...ids, id];
  }
  return ids.filter((x) => x !== id);
}

export function RotaryFocusAreasMultiSelect({
  selectedIds,
  onChange,
  disabled,
}: RotaryFocusAreasMultiSelectProps) {
  const resolvedVariant = useFieldVariant();
  const chrome = fieldChrome(resolvedVariant);

  return (
    <div className="space-y-2">
      <div>
        <Label className="text-base font-semibold">Rotary areas of focus (optional)</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Choose one or more areas that align with this project.
        </p>
      </div>
      <div className={cn('rounded-xl divide-y divide-zinc-300 dark:divide-zinc-700', chrome)}>
        {ROTARY_FOCUS_AREAS.map((area: RotaryFocusAreaDefinition) => {
          const checked = selectedIds.includes(area.id);
          return (
            <div
              key={area.id}
              className={cn(
                'flex items-start gap-3 p-4 transition-colors',
                checked && 'bg-background/50 dark:bg-white/5',
                !disabled && 'hover:bg-background/40 dark:hover:bg-white/[0.03]'
              )}
            >
              <Checkbox
                id={`rotary-${area.id}`}
                checked={checked}
                disabled={disabled}
                className="mt-0.5 shrink-0"
                onCheckedChange={(v) => onChange(toggle(selectedIds, area.id, v === true))}
              />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Label
                  htmlFor={`rotary-${area.id}`}
                  className="text-sm font-semibold cursor-pointer leading-snug block"
                >
                  {area.title}
                </Label>
                <p className="text-sm text-muted-foreground leading-relaxed">{area.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
